// deployment_class: customer_env_allowed
/**
 * CEO redesign — the drill (Increment 2). A frosted slide-over that opens from
 * any meaningful object (a need, a KPI, a priority/department card) and carries
 * the Ask-Kathedral dialog INLINE, in context (per E3 1d/2b). Every drill ends
 * in provenance ("verify me, not trust me"). Closing returns you exactly where
 * you were. No dead ends: an object that looks clickable opens a real drill.
 */
import { useState, useEffect, useRef } from 'react';
import type { Kpi, Need, CardDef, Workflow, WfState, TabRow } from './dashboard-data';
import { PHASE_DETAIL, type Deal } from './project-data';
import { useAskKathedral, type AskFn } from '../shell/ai/useAskKathedral';
import './drill.css';
import { RichText } from '../shared/ui/RichText';

/** Where a drill row "takes me" when clicked (bug_UI_1j/1L · links-to-data). */
export interface DrillNav { kind: 'module' | 'project'; key: string; }
export interface DrillRow { k: string; v?: string; tone?: 'g' | 'y' | 'r' | ''; to?: DrillNav; }
export interface DrillSection { label: string; rows?: DrillRow[]; note?: string; }
export interface DrillDef {
  icon: string; title: string; sub: string;
  /** Optional drill kind (e.g. 'milestone') — lets the host wire Prev/Next. */
  kind?: string;
  /** Context travels with the drill on the stack (so Prev/Next + stepper clicks
   *  work at any nesting depth without separate host state). */
  ctx?: { deal: string; idx: number };
  stepper?: Array<{ label: string; state: WfState; focus?: boolean }>;
  sections: DrillSection[];
  /** Optional back face (bug_UI_1c) — flip to see the live data behind this item. */
  back?: { label: string; sections: DrillSection[] };
  /** Optional call-to-action placeholder (e.g. the Yardi connect/login · bug_UI_1f). */
  cta?: { label: string; note: string };
  askCtx: string;
}

export function Drill({ def, onClose, onNavigate, onPrev, onNext, onStep, askFn }: {
  def: DrillDef; onClose: () => void; onNavigate?: (nav: DrillNav) => void;
  onPrev?: () => void; onNext?: () => void; onStep?: (index: number) => void;
  /** Test seam: override the network call (mirrors the cockpit Ask panel). */
  askFn?: AskFn;
}): JSX.Element {
  const [msgs, setMsgs] = useState<Array<{ role: 'u' | 'k'; text: string }>>([]);
  const [input, setInput] = useState('');
  // Real, governed Ask — the same POST /v3/ai/ask path the cockpit Ask panel and
  // the global overlay use (portal_context:'dashboard'; the drilled object rides
  // in context_bindings so the grounded answer is about THIS object). No canned
  // answers, no fake delay — per [[feedback_no_canned_ai_replies_ever...]].
  const { pending, send: sendAsk } = useAskKathedral(askFn);
  const ask = (q: string): void => {
    setMsgs((m) => [...m, { role: 'u', text: q }]);
    sendAsk({
      portalContext: 'dashboard',
      prompt: q,
      contextBindings: { drill_subject: def.askCtx, drill_title: def.title },
    })
      .then((reply) => setMsgs((m) => [...m, { role: 'k', text: reply }]))
      .catch((err: unknown) =>
        setMsgs((m) => [
          ...m,
          { role: 'k', text: `Sorry, I can't reach Kathedral right now. ${err instanceof Error ? err.message : ''}`.trim() },
        ]),
      );
  };
  const send = (): void => { const q = input.trim(); if (q && !pending) { ask(q); setInput(''); } };
  // Flip to the back face (bug_UI_1c) — reset whenever a new drill/def loads.
  const [flipped, setFlipped] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);
  useEffect(() => { setFlipped(false); setCtaOpen(false); }, [def]);

  // Real modal (Fable #2 · WCAG 2.1.2 / 2.4.3): move focus in on open, trap Tab,
  // Escape closes (pops the stack), restore focus to the opener on full close.
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => { opener?.focus?.(); };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const f = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!f.length) return;
      const first = f[0]; const last = f[f.length - 1]; const active = document.activeElement;
      if (!panel.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const renderSections = (sections: DrillSection[]): JSX.Element[] => sections.map((s, i) => (
    <div key={i}>
      <div className="kd-dsec">{s.label}</div>
      {s.note && <div className="kd-prov"><RichText value={s.note} /></div>}
      {s.rows?.map((r, j) => (
        r.to && onNavigate ? (
          <button className="kd-drow kd-drow-nav" key={j} onClick={() => onNavigate(r.to!)} aria-label={`Go to ${r.k}`}>
            <span>{r.k}</span>
            <span className="kd-drow-go" aria-hidden="true">→</span>
          </button>
        ) : (
          <div className="kd-drow" key={j}>
            <span>{r.k}</span>
            {r.v != null && <b style={r.tone ? { color: `var(--kd-${r.tone === 'g' ? 'green' : r.tone === 'y' ? 'amber' : 'red'})` } : undefined}>{r.v}</b>}
          </div>
        )
      ))}
    </div>
  ));

  return (
    <>
      <div className="kd-scrim" onClick={onClose} aria-hidden="true" />
      <aside ref={panelRef} tabIndex={-1} className="kd-drill" role="dialog" aria-modal="true" aria-label={def.title}>
        <div className="kd-dh">
          {/* Back to main (ui_bug2) — a header-level close that is always
              reachable, especially on mobile where the drawer is full-width and
              the scrim can't be tapped. Same action as ✕ / Escape. */}
          <button className="kd-dback" onClick={onClose} aria-label="Back to main">‹</button>
          <span className="kd-kk" style={{ width: 24, height: 24 }}>{def.icon}</span>
          <div><h3>{def.title}</h3><div className="kd-dsub">{def.sub}</div></div>
          {(onPrev || onNext) && (
            // Prev/Next walks sibling items in place (bug_UI_1k — "I like this",
            // applied to the drill).
            <div className="kd-dnav" role="group" aria-label="Previous / next">
              <button className="kd-dnavb" onClick={onPrev} disabled={!onPrev} aria-label="Previous">‹</button>
              <button className="kd-dnavb" onClick={onNext} disabled={!onNext} aria-label="Next">›</button>
            </div>
          )}
          <button className="kd-dx" onClick={onClose} aria-label="Close drill panel">✕</button>
        </div>
        <div className="kd-db">
          {/* Flip control (bug_UI_1c) — see the live data behind this milestone, then Return. */}
          {def.back && (
            <button className="kd-flipbtn" onClick={() => setFlipped((f) => !f)} aria-pressed={flipped}>
              {flipped ? '← Return to the milestone' : '⟳ See what’s live behind this'}
            </button>
          )}
          <div className="kd-flipface" key={flipped ? 'back' : 'front'}>
            {flipped && def.back ? (
              <>
                <div className="kd-dsec" style={{ marginTop: 0 }}>{def.back.label}</div>
                {renderSections(def.back.sections)}
              </>
            ) : (
              <>
                {def.stepper && (
                  // Clicking a step opens it in place / overlays its detail
                  // (bug_UI_1L "take me there"). Interactive only when onStep is wired.
                  <div className={`kd-dstepper ${onStep ? 'kd-dstepper-nav' : ''}`} aria-hidden={onStep ? undefined : true}
                    role={onStep ? 'list' : undefined} aria-label={onStep ? 'Milestones — click to open' : undefined}>
                    {def.stepper.map((s, i) => {
                      const cls = `kd-step ${s.state === 'done' ? 'done' : ''} ${s.state === 'now' ? 'now' : ''} ${s.focus ? 'focus' : ''}`;
                      const inner = (<>{s.focus && <div className="kd-here">viewing</div>}<div className="kd-sg" /><div className="kd-sd" /><div className="kd-sl">{s.label}</div></>);
                      return onStep ? (
                        <button className={`${cls} kd-step-nav`} key={i} onClick={() => onStep(i)} aria-label={`Open ${s.label} milestone`}>{inner}</button>
                      ) : (
                        <div className={cls} key={i}>{inner}</div>
                      );
                    })}
                  </div>
                )}
                {renderSections(def.sections)}
                {def.cta && (
                  // Placeholder CTA (e.g. Yardi connect · bug_UI_1f) — reveals a note; not yet wired.
                  <div className="kd-ctawrap">
                    <button className="kd-cta" onClick={() => setCtaOpen((o) => !o)} aria-expanded={ctaOpen}>{def.cta.label}</button>
                    {ctaOpen && <div className="kd-prov" style={{ marginTop: 10 }}>{def.cta.note}</div>}
                  </div>
                )}
                <div className="kd-convo">
                  {msgs.map((m, i) => m.role === 'u'
                    ? <div className="kd-bubble u" key={i}><div className="bx">{m.text}</div></div>
                    // Assistant content is model output · render as TEXT, never HTML
                    // (input-boundary discipline · matches the cockpit Ask panel · a
                    // real reply must not inject markup into the DOM).
                    : <div className="kd-bubble k" key={i}><div className="kh"><span className="kd-kk">K</span> Kathedral</div><div className="bx">{m.text}</div></div>)}
                  {pending && (
                    <div className="kd-bubble k" aria-live="polite"><div className="kh"><span className="kd-kk">K</span> Kathedral</div><div className="bx">Thinking…</div></div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="kd-dask">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            disabled={pending}
            placeholder={`Ask Kathedral about ${def.askCtx}…`} aria-label={`Ask Kathedral about ${def.askCtx}`} />
          <button onClick={send} aria-label="Send" disabled={pending}>→</button>
        </div>
      </aside>
    </>
  );
}

// ── drill builders (what SHOULD open for each object — meaningful, with data) ──
const PROV = 'Resolves to source via <b>Merkle-anchored</b> provenance · one-click-auditable. The AI assists and cites — “verify me,” not “trust me.”';

export function needDrill(n: Need): DrillDef {
  return {
    icon: '◆', title: n.t, sub: 'What needs you',
    askCtx: n.t.split('—')[0].trim(),
    sections: [
      { label: 'Detail', note: n.d },
      { label: 'Why Kathedral surfaced it', note: n.ai },
      { label: 'Provenance', note: PROV },
    ],
  };
}

export function kpiDrill(k: Kpi): DrillDef {
  // The Yardi KPI ("not connected") carries the connect/login placeholder (bug_UI_1f).
  const yardi = k.k === 'Yardi';
  return {
    icon: '▤', title: k.k, sub: 'Key figure',
    askCtx: k.k,
    sections: [
      { label: 'Value', rows: [{ k: k.k, v: k.v }] },
      { label: 'Context', note: k.s },
      { label: 'Provenance', note: PROV },
    ],
    ...(yardi
      ? { cta: {
          label: '🔌 Connect Yardi',
          note: 'Placeholder — the Yardi connector is spec’d but not yet live. Connecting reads the Voyager warehouse (Yardi Replicate / Data Connect) once a PHA licenses it; there is no buy-online API. Wiring lands with the Yardi connector build.',
        } }
      : {}),
  };
}

export function rowDrill(r: TabRow, ctx: string): DrillDef {
  return {
    icon: '▤', title: r.title, sub: ctx,
    askCtx: r.title,
    sections: [
      ...(r.rag ? [{ label: 'Status', rows: [{ k: r.title, v: r.rag[1], tone: r.rag[0] }] } as DrillSection] : []),
      { label: 'Detail', note: r.meta ?? '—' },
      { label: 'Provenance', note: PROV },
    ],
  };
}

/** A message-thread drill (ui_bug1) — a genuinely useful drawer when a thread
 *  is opened: status, participants, the actual last message, thread activity,
 *  and a click-through to the linked deal. Falls back to rowDrill for a row
 *  that carries no `thread`. */
export function messageDrill(r: TabRow, ctx: string): DrillDef {
  const t = r.thread;
  if (!t) return rowDrill(r, ctx);
  return {
    icon: '✉', title: r.title, sub: ctx,
    askCtx: r.title,
    sections: [
      { label: 'Status', rows: [{ k: t.status, v: r.right, tone: t.statusTone ?? '' }] },
      { label: 'Participants', rows: [{ k: 'From', v: t.from }, { k: 'To', v: t.to }] },
      { label: 'Last message', note: t.last },
      ...(t.extra && t.extra.length
        ? [{ label: 'In this thread', rows: t.extra } as DrillSection]
        : []),
      ...(t.deal
        ? [{ label: 'Linked deal', rows: [{ k: t.deal, to: { kind: 'project' as const, key: t.deal.toLowerCase() } }] } as DrillSection]
        : []),
      { label: 'Provenance', note: PROV },
    ],
  };
}

export function cardDrill(c: CardDef, modLabel: string): DrillDef {
  return {
    icon: c.icon, title: c.name, sub: modLabel,
    askCtx: c.name,
    sections: [
      { label: 'Status', rows: [{ k: c.name, v: c.rag[1], tone: c.rag[0] }] },
      { label: 'Detail', note: c.meta },
      // Every module conveys WHICH projects it touches (bug_UI_1f) — and each is
      // a live link that TAKES YOU THERE (bug_UI_1j).
      ...(c.projects && c.projects.length
        ? [{ label: `Projects it belongs to (${c.projects.length})`, rows: c.projects.map((p) => ({ k: p, to: { kind: 'project' as const, key: p.toLowerCase() } })) } as DrillSection]
        : []),
      { label: 'Provenance', note: PROV },
    ],
  };
}

const TICK_EXPLAIN: Record<string, string> = {
  Progress: 'How far this deal has moved through its 9-phase lifecycle, weighted by completed milestones.',
  Phase: 'The current lifecycle stage (of 9): Planning → … → Stabilized.',
  Budget: 'Total development cost committed to date, reconciled to the signed sources & uses.',
  DSCR: 'Debt-service coverage ratio — net operating income ÷ debt service. Your floor is 1.20.',
  Status: 'Where the deal stands in review / governance right now.',
  Partner: 'The development partner and their track record on the Kathedral-Verified marketplace.',
  'LIHTC equity': 'Low-Income Housing Tax Credit equity — typically the largest source in a 9% deal.',
  'Soft debt': 'Subordinate / soft financing layered behind the senior debt.',
  'Bond proceeds': 'Tax-exempt bond proceeds funding the deal.',
  Equity: 'Sponsor / investor equity contribution.',
  'Deferred fee': 'Developer fee deferred to balance sources & uses.',
  'Financing gap': 'Unfilled funding need that must be closed before the deal can proceed.',
};

/** The "what it means" copy for a ticker metric — shared with the ticker hover
 *  preview (bug_UI_1i) so the preview shows real drill-in data, not just a value. */
export function tickExplain(metric: string): string {
  return TICK_EXPLAIN[metric] ?? 'A live figure in this project — one-click-auditable to its source.';
}

export function tickDrill(metric: string, value?: string): DrillDef {
  return {
    icon: '▦', title: metric, sub: 'Data Ticker · what it means',
    askCtx: metric,
    sections: [
      ...(value ? [{ label: 'Value', rows: [{ k: metric, v: value }] } as DrillSection] : []),
      { label: 'What it means', note: TICK_EXPLAIN[metric] ?? 'A live figure in this project — one-click-auditable to its source.' },
      { label: 'Provenance', note: PROV },
    ],
  };
}

/**
 * A lifecycle milestone drill (bug_UI_1c): clicking a phase on the project
 * timeline opens the milestone with its position, state, and the full 9-phase
 * stepper for context. Grounded — no invented per-phase specifics; the richer
 * per-milestone workflow data + the front/back flip is the next increment.
 */
export function milestoneDrill(
  deal: Deal, phases: readonly string[], clickedIdx: number,
): DrillDef {
  const dealName = deal.name;
  const phaseIdx = deal.phaseIdx;
  const name = phases[clickedIdx] ?? 'Milestone';
  const det = PHASE_DETAIL[name];
  const state = clickedIdx < phaseIdx ? 'Completed' : clickedIdx === phaseIdx ? 'In progress · current' : 'Upcoming';
  const tone: 'g' | 'y' | '' = clickedIdx < phaseIdx ? 'g' : clickedIdx === phaseIdx ? 'y' : '';
  const stepState = (i: number): WfState => (i < phaseIdx ? 'done' : i === phaseIdx ? 'now' : 'todo');
  return {
    icon: '◷', title: name, kind: 'milestone', ctx: { deal: deal.key, idx: clickedIdx },
    sub: `${dealName} · Milestone ${clickedIdx + 1} of ${phases.length} · ${state}`,
    askCtx: `the ${name} milestone on ${dealName}`,
    // focus flags the clicked milestone so the stepper marks WHERE you are (bug_UI_1h).
    stepper: phases.map((p, i) => ({ label: p, state: stepState(i), focus: i === clickedIdx })),
    sections: [
      { label: 'Status', rows: [{ k: name, v: state, tone }] },
      // Everything IN this milestone (bug_UI_1h) — fills the panel with real data.
      ...(det
        ? [
            { label: 'What this milestone is', note: det.summary } as DrillSection,
            { label: `Key activities (${det.activities.length})`, rows: det.activities.map((a) => ({ k: a })) } as DrillSection,
            { label: 'Gate to advance', rows: [{ k: det.gate, tone: (clickedIdx <= phaseIdx ? 'g' : '') as 'g' | '' }] } as DrillSection,
            { label: 'Accountable owner', rows: [{ k: det.owner }] } as DrillSection,
          ]
        : []),
      { label: 'Where it sits', note: `Phase ${clickedIdx + 1} of ${phases.length}. ${dealName} is currently in <b>${phases[phaseIdx]}</b> (phase ${phaseIdx + 1}) — this milestone is <b>${state.toLowerCase()}</b>.` },
      { label: 'Provenance', note: PROV },
    ],
    // Flip to the live figures behind this milestone (bug_UI_1c).
    back: {
      label: `What's live behind ${name}`,
      sections: [
        { label: `${dealName} — live figures`, rows: [
          { k: 'Budget (TDC)', v: deal.budget },
          { k: 'DSCR', v: deal.dscr },
          { k: 'Units', v: String(deal.units) },
          { k: 'Status', v: deal.status, tone: deal.rag[0] as 'g' | 'y' | 'r' | '' },
          { k: 'Partner', v: deal.partner.split(' · ')[0] },
        ] },
        { label: `Next actions (${deal.next.length})`, rows: deal.next.map((n) => ({ k: n })) },
        { label: 'Provenance', note: PROV },
      ],
    },
  };
}

export function workflowDrill(w: Workflow): DrillDef {
  const total = w.steps.length;
  const done = w.steps.filter((s) => s[1] === 'done').length;
  const nowIdx = w.steps.findIndex((s) => s[1] === 'now');
  const curi = nowIdx >= 0 ? nowIdx : done;
  const mark = (st: WfState): string => (st === 'done' ? '✓' : st === 'now' ? '▸' : '○');
  return {
    icon: '⛓', title: w.name,
    sub: `Workflow · step ${Math.min(curi + 1, total)} of ${total}${w.due ? ' · ' + w.due : ''}`,
    askCtx: w.name,
    stepper: w.steps.map((s) => ({ label: s[0], state: s[1] })),
    sections: [
      { label: 'Steps & ownership', rows: w.steps.map((s) => ({ k: `${mark(s[1])} ${s[0]}`, v: s[2], tone: (s[1] === 'done' ? 'g' : '') as 'g' | '' })) },
      { label: 'Next action', rows: [{ k: w.steps[curi]?.[0] ?? 'Process complete', v: w.steps[curi]?.[2] ?? '—' }] },
      { label: 'Provenance', note: 'This workflow runs off a templatized checklist · every step assignable, precedent→successor linked, each completion <b>Merkle-anchored</b>. Survives CEO turnover — the process is institutional, not tribal.' },
    ],
  };
}
